import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedCompanyMember,
  DecoratedMemberCompany,
  UndecoratedCompanyMember,
} from '@flemingmetrics/api-client';
import { mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey, Identifier } from '@/store/utils';

type CompanyMembersState = {
  byId: Record<string, UndecoratedCompanyMember>;
  byCompanyId: Record<string, string[]>;
  byProfileId: Record<string, string[]>;
};

const initialState: CompanyMembersState = {
  byId: {},
  byCompanyId: {},
  byProfileId: {},
};

export const companyMembersSlice = createSlice({
  name: 'companyMembers',
  initialState,
  reducers: {
    addCompanyMember: (
      state,
      action: PayloadAction<DecoratedCompanyMember>
    ) => {
      return getNextState(state, [action.payload]);
    },
    setCompanyMembers: (
      state,
      action: PayloadAction<DecoratedCompanyMember[]>
    ) => {
      return getNextState(state, action.payload);
    },
    setMemberCompanies: (
      state,
      action: PayloadAction<DecoratedMemberCompany[]>
    ) => {
      return getNextState(state, action.payload);
    },
    deleteCompanyMember: (
      state,
      action: PayloadAction<{ companyId: number; profileId: number }>
    ) => {
      const { companyId, profileId } = action.payload;
      const key = buildCompanyMemberKey(companyId, profileId);
      const byId = { ...state.byId };
      const byCompanyId = { ...state.byCompanyId };
      const byProfileId = { ...state.byProfileId };
      byCompanyId[companyId] = byCompanyId[companyId].filter((k) => k !== key);
      byProfileId[profileId] = byProfileId[profileId].filter((k) => k !== key);
      delete byId[key];
      return { byId, byCompanyId, byProfileId };
    },
  },
  extraReducers: (builder) => {
    builder.addDefaultCase(() => {});
  },
});

function formatCompanyMember(
  entry: DecoratedCompanyMember | DecoratedMemberCompany
): UndecoratedCompanyMember {
  if ('company' in entry) {
    const { company, ...companyMember } = entry;
    return companyMember;
  } else {
    const { profile, ...companyMember } = entry;
    return companyMember;
  }
}

function getNextState(
  state: CompanyMembersState,
  companyMembers: (DecoratedCompanyMember | DecoratedMemberCompany)[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  const byProfileId = { ...state.byProfileId };
  companyMembers.forEach((cm) => {
    const { companyId, profileId } = cm;
    const key = buildCompanyMemberKey(companyId, profileId);
    byId[key] = formatCompanyMember(cm);
    if (!byCompanyId[companyId]) byCompanyId[companyId] = [];
    if (!byProfileId[profileId]) byProfileId[profileId] = [];
    byCompanyId[companyId] = mergeWithoutDuplicates(byCompanyId[companyId], [
      key,
    ]);
    byProfileId[profileId] = mergeWithoutDuplicates(byProfileId[profileId], [
      key,
    ]);
  });
  return { byId, byCompanyId, byProfileId };
}

export function buildCompanyMemberKey(
  companyId: Identifier,
  profileId: Identifier
) {
  return buildCompositeKey(companyId, profileId);
}

export const {
  addCompanyMember,
  setCompanyMembers,
  setMemberCompanies,
  deleteCompanyMember,
} = companyMembersSlice.actions;

export default companyMembersSlice.reducer;
